<template>
  <div
    role="radio"
    class="adn-radio-button"
    :class="{ 'adn-radio-button--disabled': isDisabled }"
    data-test="radio-button-wrapper"
    @click="updateValue()"
  >
    <label class="adn-radio-button__label">
      <input
        v-model="internal_selectedValue"
        tabindex="-1"
        data-test="radio-button"
        type="radio"
        :value="value"
        :name="groupName"
        :disabled="isDisabled"
      />
      <span tabindex="-1" class="adn-radio-button__custom-input"></span>
      <slot></slot>
    </label>
  </div>
</template>

<script>
export default {
  name: 'AdnRadioButton',
  props: {
    initialValue: {
      type: [String, Number, Boolean],
      default: null,
    },
    value: {
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    groupName: {
      type: String,
      required: true,
    },
  },
  emits: ['input', 'update:modelValue'],
  data() {
    return {
      stateDisabled: false,
      internal_selectedValue: null,
    }
  },
  computed: {
    isDisabled() {
      return this.disabled || this.stateDisabled
    },
  },
  watch: {
    initialValue(newValue) {
      this.internal_selectedValue = newValue
    },
  },
  mounted() {
    this.internal_selectedValue = this.initialValue
    this.stateDisabled = this.disabled
  },
  methods: {
    updateValue() {
      if (this.isDisabled) return
      this.$emit('input', this.value)
      this.$emit('update:modelValue', this.value)
    },
    setDisabled(newValue) {
      if (this.disabled) {
        return
      }
      this.stateDisabled = newValue
    },
  },
}
</script>

<style scoped lang="scss">
.adn-radio-button {
  cursor: pointer;
  display: flex;
  transition: transform 0.14s ease;

  .adn-radio-button__label {
    display: flex;
    align-items: center;
    transition: transform 0.14s ease;
  }

  &.adn-radio-button--disabled {
    cursor: not-allowed;

    .adn-radio-button__label {
      cursor: not-allowed;
      color: var(--color-inactive-dark);
    }
  }

  input {
    width: 0;
    height: 0;
    opacity: 0;

    & + .adn-radio-button__custom-input {
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      transition: all ease 0.2s;
      margin-right: 0.8rem;
      height: 2.4rem;
      width: 2.4rem;
      border: solid 1px var(--color-text-light);
      border-radius: 2rem;

      &::after {
        content: '';
        display: block;
        height: 0;
        width: 0;
        background-color: var(--color-main);
        border-radius: 1.4rem;
        transition: all 0.1s ease;
      }
    }

    &:checked + .adn-radio-button__custom-input {
      transition: all 0.1s ease;
      border-color: var(--color-main);
      background-color: var(--color-main);

      &::after {
        height: 0;
        width: 0;
      }

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        height: 0.8rem;
        width: 0.8rem;
        background-color: white;
        border-radius: 50%;
        z-index: 1;
      }
    }

    &:disabled + .adn-radio-button__custom-input {
      cursor: not-allowed;
      border: solid 1px var(--color-inactive-dark);

      &::after {
        background-color: var(--color-inactive-dark);
      }
    }

    &:hover + .adn-radio-button__custom-input {
      transition: all 0.1s ease;
      border-color: var(--color-main);
    }
  }
}
</style>
